import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Shield } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { USER_ROLES, type UserRole } from '@praxara/shared/constants';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

interface UserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive: boolean;
  mfaEnabled: boolean;
  isSuperAdmin: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  tenant: { id: string; name: string; slug: string; plan: string };
}

export function PlatformUsersPage() {
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const [q, setQ] = useState('');

  const users = useQuery({
    queryKey: ['platform-admin', 'users', q],
    queryFn: async () => (await api.get(`/api/platform-admin/users?q=${encodeURIComponent(q)}`)).data,
  });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      (await api.patch(`/api/platform-admin/users/${id}`, body)).data,
    onSuccess: () => {
      toast.success('User updated');
      qc.invalidateQueries({ queryKey: ['platform-admin', 'users'] });
    },
    onError: (err) => toast.error('Update failed', extractApiError(err).message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Users</h1>
        <p className="text-sm text-muted-foreground">All accounts across all tenants.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name..." className="pl-8" />
      </div>

      {users.isLoading ? (
        <Skeleton className="h-64" />
      ) : !users.data?.items?.length ? (
        <EmptyState icon={Search} title="No users match" description="Try a different search." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {users.data.items.map((u: UserRow) => (
                <div key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{u.firstName} {u.lastName}</span>
                      {u.isSuperAdmin && (
                        <Badge variant="secondary" className="gap-1">
                          <Shield className="h-3 w-3" /> Super
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {u.email} ? <span className="text-foreground">{u.tenant.name}</span> ({u.tenant.slug})
                    </div>
                  </div>

                  <select
                    value={u.role}
                    onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value } })}
                    className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  >
                    {USER_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>

                  <Badge variant={u.mfaEnabled ? 'success' : 'secondary'}>MFA {u.mfaEnabled ? 'on' : 'off'}</Badge>

                  <button
                    onClick={() => update.mutate({ id: u.id, body: { isActive: !u.isActive } })}
                    className="text-xs"
                  >
                    <Badge variant={u.isActive ? 'success' : 'destructive'}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </button>

                  {u.id !== currentUser?.id && (
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            u.isSuperAdmin
                              ? `Remove superadmin flag from ${u.email}?`
                              : `Grant PLATFORM superadmin to ${u.email}? This is a platform-wide privilege — not tenant ADMIN.`,
                          )
                        )
                          update.mutate({ id: u.id, body: { isSuperAdmin: !u.isSuperAdmin } });
                      }}
                      className="text-xs"
                    >
                      <Badge variant={u.isSuperAdmin ? 'info' : 'secondary'}>
                        {u.isSuperAdmin ? 'revoke super' : 'promote'}
                      </Badge>
                    </button>
                  )}

                  <div className="w-32 text-right text-[10px] text-muted-foreground">
                    {u.lastLoginAt ? `Last seen ${formatDate(u.lastLoginAt)}` : 'never signed in'}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
